import React, { useState, useMemo, useEffect, useImperativeHandle } from 'react'
import RichTextEditor from '../../../components/common/RichTextEditor'
import RichTextEditorArea from '../../../components/common/RichTextEditorArea'
import { getSession } from '../../../getSession'
import { getMailSmsDLTAccount, getSenderIdDLT, getDLTTemplates, getDLTTemplateDetails } from '../../../utils/lead'

const SMSForm = React.forwardRef(({ lead }, ref) => {
  const [tab, setTab] = useState('indian') // 'indian' or 'nonIndian'
  const [selectedNumbers, setSelectedNumbers] = useState([])
  const [senders, setSenders] = useState([])
  const [selectedSender, setSelectedSender] = useState('')
  const [apps, setApps] = useState(['--Select SenderID--'])
  const [selectedApp, setSelectedApp] = useState(apps[0])
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [senderId, setSenderId] = useState('')
  const [message, setMessage] = useState('')
  const [isUnicode, setIsUnicode] = useState(false)
  const [urlTrack, setUrlTrack] = useState(false)
  const [trackingUrl, setTrackingUrl] = useState('')

  const toggleNumber = (number) => {
    setSelectedNumbers(prev => prev.includes(number) ? prev.filter(n => n !== number) : [...prev, number])
  }

  // derive possible mobile numbers from lead object
  const deriveLeadNumbers = () => {
    if (!lead) return [];
    const candidates = [
      lead.MobileNo,
      lead.MobileNo1,
      lead.MobileNo2
    ];
    const set = new Set();
    candidates.forEach(c => {
      if (!c) return;
      const s = String(c).trim();
      // normalize to digits only
      const digits = s.replace(/[^0-9+]/g, '');
      if (digits) set.add(digits);
    });
    return Array.from(set);
  };

  const leadNumbers = deriveLeadNumbers();

  useEffect(() => {
    if (!leadNumbers || !leadNumbers.length) return;
    setSelectedNumbers(prev => {
      const merged = new Set(prev);
      leadNumbers.forEach(n => merged.add(n));
      return Array.from(merged);
    });
  }, [leadNumbers.join('|')]);

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      const senderMatch = (selectedApp && typeof selectedApp === 'string') ? selectedApp.match(/\(([^)]+)\)\s*$/) : null;
      const SenderID = senderMatch ? senderMatch[1] : (selectedApp || '');
      const SenderName = (selectedApp && typeof selectedApp === 'string') ? (selectedApp.replace(/\s*\([^)]*\)\s*$/, '').trim()) : selectedApp;
      const EnquiryLeadId = lead?.ID ?? lead?.LeadId ?? lead?.id ?? '';
      // assemble destination numbers for international form
      const dest = Array.isArray(selectedNumbers) ? selectedNumbers.join(',') : '';
      const MobileNo = selectedNumbers[0] || '';
      const MobileNo1 = selectedNumbers[1] || '';
      const MobileNo2 = selectedNumbers[2] || '';
      const SmsType = message && message.trim() ? 'compose' : 'template';
      const IsNonIndian = tab === 'nonIndian';
      // prefer the explicit senderId input when on nonIndian tab
      const ExplicitSenderId = IsNonIndian && senderId ? senderId : SenderID || '';

      return {
        EnquiryLeadId,
        EntityType: 'lead',
        Account: selectedSender || '',
        SenderName: SenderName || '',
        SenderID: ExplicitSenderId || SenderID || '',
        Dest: dest,
        Template: selectedTemplate || '',
        Message: message || '',
        IsUnicode: !!isUnicode,
        UrlTrack: !!urlTrack,
        TrackingUrl: trackingUrl || '',
        MobileNo,
        MobileNo1,
        MobileNo2,
        IsNonIndian,
        SmsType
      };
    }
  }), [selectedApp, selectedSender, selectedNumbers, selectedTemplate, message, isUnicode, lead]);

  const charCount = useMemo(() => message.length, [message])
  const maxChars = tab === 'indian' ? (isUnicode ? 70 : 160) : 2000

  useEffect(() => {
    let mounted = true;
    const fetchAccounts = async () => {
      try {
        const { userId, TokenId, parentId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { ParentId: parentId, IsSms: true },
          BroadcastName: ''
        };
        const resp = await getMailSmsDLTAccount(payload);
        let list = resp?.Details ?? resp?.d ?? resp ?? [];
        if (list && list.data) list = list.data;
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Accounts ?? list;
        }
        const accountsArray = Array.isArray(list) ? list.map(item => {
          if (!item) return null;
          if (typeof item === 'string') return { Code: item, Text: item };
          return {
            Code: String(item?.Code ?? item?.UserID ?? item?.Value ?? ''),
            Text: item?.Text ?? item?.AccountName ?? item?.Name ?? ''
          };
        }).filter(Boolean) : [];
        if (mounted) {
          // always include a placeholder option
          const options = [{ Code: '', Text: '--Select Accounts--' }, ...accountsArray];
          setSenders(options);
          setSelectedSender(options[0].Code);
        }
      } catch (err) {
        console.warn('Failed to load DLT accounts:', err?.message || err);
      }
    };
    fetchAccounts();
    return () => { mounted = false };
  }, []);

  // Additional fetch: Get sender ids (DLT) and prefer them for the Sender dropdown.
  useEffect(() => {
    let mounted = true;
    const fetchSenderIds = async () => {
      try {
        const { userId, TokenId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { AccUserId: userId },
          BroadcastName: ''
        };
        const resp = await getSenderIdDLT(payload);
        let list = resp ?? [];
        if (list && list.data) list = list.data;
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Accounts ?? list.Details ?? list;
        }
        const sendersArray = Array.isArray(list) ? list.map(item => {
          if (!item) return '';
          if (typeof item === 'string') return item;
          // CommonEntity: { Code, Text }
          const code = item.Code ?? item.Sender_ID ?? item.SenderId ?? '';
          const text = item.Text ?? item.Sender_Name ?? item.SenderName ?? '';
          return text ? `${text}${code ? ' (' + code + ')' : ''}` : (code || JSON.stringify(item));
        }).filter(Boolean) : [];

        // Bind these results to the App dropdown instead of Sender
        if (mounted && sendersArray.length) {
          setApps(['--Select App--', ...sendersArray]);
          setSelectedApp(sendersArray[0]);
        }
      } catch (err) {
        console.warn('Failed to load SenderIdDLT:', err?.message || err);
      }
    };
    fetchSenderIds();
    return () => { mounted = false };
  }, []);

  // Fetch DLT templates whenever selectedSender changes (or on mount)
  useEffect(() => {
    let mounted = true;
    const fetchTemplates = async () => {
      try {
        const { userId, TokenId } = getSession();
        // pass the selected app display text (sender name) to the API.
        // If the option is formatted like "Name (CODE)", extract the Name part; otherwise use the full string.
        let senderCode = '';
        if (selectedApp && typeof selectedApp === 'string') {
          const m = selectedApp.match(/^(.*)\s*\(([^)]+)\)\s*$/);
          senderCode = m ? m[1].trim() : (selectedApp === '--Select App--' || selectedApp === '' ? '' : selectedApp);
        }

        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { SenderID: senderCode, UserId: userId, isenquiry: false, islead: true },
          BroadcastName: ''
        };

        const resp = await getDLTTemplates(payload);
        let list = resp ?? [];
        if (list && list.data) list = list.data;
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Templates ?? list;
        }

        const tpl = Array.isArray(list) ? list.map(item => {
          if (!item) return null;
          if (typeof item === 'string') return { value: item, label: item };
          const value = item.Code ?? item.DLT_TemplateID ?? item.TemplateId ?? '';
          const label = item.Text ?? item.TemplateName ?? item.Text ?? JSON.stringify(item);
          return { value: value || label, label };
        }).filter(Boolean) : [];

        if (mounted) {
          setTemplates(tpl);
          if (tpl.length) setSelectedTemplate(tpl[0].value);
        }
      } catch (err) {
        console.warn('Failed to load DLT templates:', err?.message || err);
      }
    };
    fetchTemplates();
    return () => { mounted = false };
  }, [selectedApp]);

  // When a template is selected, fetch its details and bind template text to message
  useEffect(() => {
    let mounted = true;
    const fetchTemplateDetails = async () => {
      try {
        if (!selectedTemplate) return;
        const { userId, TokenId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { DLTTemplateId: selectedTemplate, UserId: userId },
          BroadcastName: ''
        };
        const resp = await getDLTTemplateDetails(payload);
        if (!mounted) return;
        if (Array.isArray(resp) && resp.length) {
          const tpl = resp[0] || {};
          const templateText = tpl.TemplateText ?? tpl.MailContent ?? tpl.Template ?? tpl.TemplateText ?? '';
          setMessage(templateText || '');
          // If IsUnicode flag present, reflect it
          if (typeof tpl.IsUnicode !== 'undefined') setIsUnicode(!!tpl.IsUnicode);
        }
      } catch (err) {
        console.warn('Failed to load template details:', err);
      }
    };

    fetchTemplateDetails();
    return () => { mounted = false };
  }, [selectedTemplate]);

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="flex border-b">
          <button onClick={() => setTab('indian')} className={`px-4 py-2 -mb-px ${tab==='indian'? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-600'}`}>Indian SMS</button>
          <button onClick={() => setTab('nonIndian')} className={`px-4 py-2 -mb-px ${tab==='nonIndian'? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-600'}`}>Non-Indian SMS</button>
        </div>
      </div>

      {tab === 'indian' && (
        <div>
          <h4 className="text-lg font-medium mb-3">Choose Mobile Numbers</h4>
          <div className="mb-3">
            {leadNumbers && leadNumbers.length ? (
              leadNumbers.map((num, idx) => (
                <label key={idx} className="flex items-center gap-3 mb-2">
                  <input type="checkbox" checked={selectedNumbers.includes(num)} onChange={() => toggleNumber(num)} className="w-4 h-4 rounded-[4px]" />
                  <span>{num}</span>
                </label>
              ))
            ) : (
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={selectedNumbers.includes('9729723338')} onChange={() => toggleNumber('9729723338')} className="w-4 h-4 rounded-[4px]" />
                <span>9729723338</span>
              </label>
            )}
          </div>

          <div className="p-3 mb-3 border rounded bg-blue-50 text-sm text-blue-800">Only DLT registered entities can send SMS. To know more <a className="underline">Click here !</a></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Sender</label>
              <select value={selectedSender} onChange={e => setSelectedSender(e.target.value)} className="w-full border rounded px-3 py-2">
                {senders.map(s => <option key={s.Code} value={s.Code}>{s.Text}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">App</label>
              <select value={selectedApp} onChange={e => setSelectedApp(e.target.value)} className="w-full border rounded px-3 py-2">
                {apps.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Select Template</label>
              <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} className="w-full border rounded px-3 py-2">
                {templates.length ? templates.map(t => <option key={t.value} value={t.value}>{t.label}</option>) : <option value="">--Select Template--</option>}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Message</label>
              <div className="relative">
                <RichTextEditorArea className="w-full border rounded px-3 py-2 h-28" value={message} onChange={e => setMessage(e.target.value)} placeholder="Message" rows={4} />
                {/* <div className="absolute bottom-2 right-2 text-xs text-gray-500">{charCount} / {maxChars}</div> */}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2"><input className='rounded-[4px]' type="checkbox" checked={isUnicode} onChange={() => setIsUnicode(!isUnicode)} /> IsUnicode</label>
          </div>

        </div>
      )}

      {tab === 'nonIndian' && (
        <div>
          <div className="mb-3 text-sm text-gray-700">Note: Your wallet balance should be at least 1.5 times of the estimated batch cost to perform this action</div>
          <h4 className="text-lg font-medium mb-3">Choose Mobile Numbers</h4>
          <div className="mb-3">
              {leadNumbers && leadNumbers.length ? (
                leadNumbers.map((num, idx) => (
                  <label key={idx} className="flex items-center gap-3 mb-2">
                    <input type="checkbox" checked={selectedNumbers.includes(num)} onChange={() => toggleNumber(num)} className="w-4 h-4 rounded-[4px]" />
                    <span>{num}</span>
                  </label>
                ))
              ) : (
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={selectedNumbers.includes('9729723338')} onChange={() => toggleNumber('9729723338')} className="w-4 h-4 rounded-[4px]" />
                  <span>9729723338</span>
                </label>
              )}
          </div>

          <div className="grid grid-cols-1 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">SenderId</label>
              <input value={senderId} onChange={e => setSenderId(e.target.value)} className="w-full border rounded px-3 py-2" placeholder="Enter SenderId" />
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Compose SMS</label>
              <RichTextEditorArea className="w-full border rounded px-3 py-2 h-28" value={message} onChange={e => setMessage(e.target.value)} placeholder="Compose SMS" rows="4" />
              {/* <div className="text-xs text-gray-500 mt-1">{charCount} / {maxChars}</div> */}
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">IsUnicode: <span className="ml-2 font-medium">{isUnicode ? 'True' : 'False'}</span></label>
              <div>
                <label className="flex items-center gap-2 rounded-[4px]"><input className='rounded-[4px]' type="checkbox" checked={urlTrack} onChange={() => setUrlTrack(!urlTrack)} /> UrlTrack</label>
                {urlTrack && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={trackingUrl}
                      onChange={e => setTrackingUrl(e.target.value)}
                      placeholder="Enter tracking URL"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
});

export default SMSForm
